import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { Service } from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import {
  Carrier,
  carrierToTrademarkText,
} from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import {
  CarrierAccountType,
  EasyPostCarrierType,
  EasypostOAuthStatus,
  ICarrierAccount,
  RedoCarrierName,
  redoCarrierNameToCarrier,
  supportedCarriersForCarrierAccountType,
  UspsOAuthStatus,
} from "@redotech/redo-fulfillment-rate-common/carrier-account";
import { RateProvider } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import { assertNever } from "@redotech/util/type";
import { memo, useContext, useMemo, useState } from "react";
import { RedoButton } from "../../../arbiter-components/buttons/redo-button";
import { RedoButtonDropdown } from "../../../arbiter-components/buttons/redo-dropdown-button";
import {
  RedoCommandMenu,
  RedoCommandMenuItem,
} from "../../../arbiter-components/command-menu/redo-command-menu";
import { RedoModal } from "../../../arbiter-components/modal/redo-modal";
import {
  RedoTable,
  RedoTableLayout,
} from "../../../arbiter-components/tables/redo-table";
import { RedoTablePaddedCell } from "../../../arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "../../../arbiter-components/tables/redo-table-header-cells";
import { RedoToggle } from "../../../arbiter-components/toggle/redo-toggle";
import { TeamContext } from "../../../context/team-context";
import { Flex } from "../../../flex";
import { LoadingRedoAnimation } from "../../../loading-redo-animation";
import { Text } from "../../../text";
import { LogoSize, ShippingLogo } from "../../common/shipping-logo";
import {
  AddCarrierForm,
  AddCarrierModal,
  carrierSpecificInput,
} from "./add-carrier-modal";
import {
  CarrierInput,
  CarrierInputForm,
  CarrierInputValue,
} from "./carrier-input";
import { carrierCardLabel, carrierCardLogoCarrier } from "./carrier-labels";
import { getFilteredCarrierOptions } from "./carrier-options";
import * as styles from "./carriers.module.css";

export interface ServiceLevelToggle {
  carrier: Carrier;
  service: Service;
  serviceDisplayName: string;
  enabled: boolean;
}

export interface CarrierServiceToggles {
  carrierAccountId?: string;
  carrier?: Carrier;
  serviceLevels: ServiceLevelToggle[];
  enabledCount: number;
  totalCount: number;
}

export const CarriersTable = memo(function CarriersTable({
  carrierAccountType,
  savedCarriers,
  carrierInputDefault,
  tableTitle,
  handleAuthenticateCarrier,
  handleDeleteCarrier,
  handleAddCarrier,
  createTestLabelPending,
  createTestLabelHandler,
  onOpenFedexMfaModal,
  onOpenEasypostFedexMfaForCreate,
  onUseMfaBackup,
  serviceToggles,
  onServiceLevelToggle,
  handleVerifyContractRates,
  handleUnverifyContractRates,
  verifyContractRatesPending,
  unverifyContractRatesPending,
  sendcloudByoaEnabled = false,
  allowedCarriers,
  presentation = "table",
}: {
  carrierAccountType: CarrierAccountType;
  savedCarriers: ICarrierAccount[] | null;
  carrierInputDefault: CarrierInputValue;
  tableTitle: string;
  handleAuthenticateCarrier: (carrierAccount: ICarrierAccount) => void;
  handleDeleteCarrier: (carrierAccount: ICarrierAccount) => void;
  handleAddCarrier: (
    carrier: CarrierInputValue,
  ) => Promise<{ success: boolean }>;
  createTestLabelPending?: boolean;
  createTestLabelHandler?: (carrierAccount: ICarrierAccount) => void;
  onOpenFedexMfaModal?: () => void;
  onOpenEasypostFedexMfaForCreate?: (carrierType: EasyPostCarrierType) => void;
  onUseMfaBackup?: (carrierAccount: ICarrierAccount) => void;
  serviceToggles?: Map<string, CarrierServiceToggles>;
  onServiceLevelToggle?: (
    key: string,
    carrier: Carrier,
    service: Service,
    enabled: boolean,
  ) => void;
  handleVerifyContractRates?: (
    carrierAccount: ICarrierAccount,
  ) => Promise<void>;
  handleUnverifyContractRates?: (
    carrierAccount: ICarrierAccount,
  ) => Promise<void>;
  verifyContractRatesPending?: boolean;
  unverifyContractRatesPending?: boolean;
  sendcloudByoaEnabled?: boolean;
  /** Restrict to these carrier names (accounts shown AND addable options); absent = all. */
  allowedCarriers?: readonly RedoCarrierName[];
  /** "cards" renders per-variant cards (vendor pages); default is the legacy table. */
  presentation?: "table" | "cards";
}) {
  const newCarrier = useInput(CarrierInput, carrierInputDefault);

  const [addCarrierModalOpen, setAddCarrierModalOpen] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState<RedoCarrierName>();
  const [carrierToDelete, setCarrierToDelete] = useState<ICarrierAccount>();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const onCloseAddCarrierModal = useHandler(() => {
    setAddCarrierModalOpen(false);
    setSelectedCarrier(undefined);
    newCarrier.setValue(carrierInputDefault);
  });

  const carrierOptions = useMemo(() => {
    const supported =
      supportedCarriersForCarrierAccountType(carrierAccountType);
    return allowedCarriers
      ? supported.filter((carrier) => allowedCarriers.includes(carrier))
      : supported;
  }, [carrierAccountType, allowedCarriers]);

  const visibleCarriers = useMemo(() => {
    if (!savedCarriers || !allowedCarriers) {
      return savedCarriers;
    }
    return savedCarriers.filter((account) =>
      allowedCarriers.includes(account.redoCarrierName),
    );
  }, [savedCarriers, allowedCarriers]);

  const toggleRowExpansion = useHandler((key: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  });

  // Same divert rules as the modal's picker cards (FedEx MFA flows); everyone
  // else gets the add form expanded inline in the variant's card.
  const [addingVariant, setAddingVariant] = useState<RedoCarrierName>();
  const [addSubmitting, setAddSubmitting] = useState(false);

  const onAddVariant = useHandler((carrier: RedoCarrierName) => {
    if (
      (carrier === RedoCarrierName.FEDEX ||
        carrier === RedoCarrierName.FEDEX_SMARTPOST) &&
      !!onOpenEasypostFedexMfaForCreate
    ) {
      onOpenEasypostFedexMfaForCreate(
        carrier === RedoCarrierName.FEDEX
          ? EasyPostCarrierType.FEDEX
          : EasyPostCarrierType.FEDEX_SMARTPOST,
      );
      return;
    }
    if (carrier === RedoCarrierName.FEDEX && !!onOpenFedexMfaModal) {
      onOpenFedexMfaModal();
      return;
    }
    setAddingVariant(carrier);
    newCarrier.setValue(carrierInputDefault);
    newCarrier.inputs.redoCarrierName.setValue(carrier);
    newCarrier.inputs.name.setValue(carrierCardLabel(carrier));
  });

  const onCancelAdd = useHandler(() => {
    setAddingVariant(undefined);
    newCarrier.setValue(carrierInputDefault);
  });

  const onSubmitAdd = useHandler(async () => {
    setAddSubmitting(true);
    try {
      const { success } = await handleAddCarrier(newCarrier.value);
      if (success) {
        setAddingVariant(undefined);
        newCarrier.setValue(carrierInputDefault);
      }
    } finally {
      setAddSubmitting(false);
    }
  });

  const addFormValid =
    addingVariant !== undefined &&
    newCarrier.inputs.name.allErrors.length === 0 &&
    carrierSpecificInput(newCarrier).allErrors.length === 0;

  // Cards honor the same team-settings availability gates as the modal picker;
  // variants with existing accounts stay visible even when no longer addable.
  const team = useContext(TeamContext);
  const addableCardVariants = useMemo(
    () =>
      getFilteredCarrierOptions({
        carrierOptions,
        checkoutOptimizationEnabled:
          team?.settings?.checkoutOptimization?.enabled,
        newOmsEnabled: team?.settings?.outboundLabels?.newOmsEnabled,
        returnsEnabled: team?.settings?.returns?.enabled,
        sendcloudByoaEnabled,
      }),
    [carrierOptions, team, sendcloudByoaEnabled],
  );
  const cardVariants = useMemo(
    () => [
      ...new Set([
        ...addableCardVariants,
        ...(visibleCarriers ?? []).map((account) => account.redoCarrierName),
      ]),
    ],
    [addableCardVariants, visibleCarriers],
  );

  const rowsExpansions = useMemo(() => {
    if (!serviceToggles || !visibleCarriers) {
      return undefined;
    }

    const expansions: Record<
      number,
      { expanded: boolean; numExpansions: number }
    > = {};
    for (const [index, carrier] of visibleCarriers.entries()) {
      const key = carrier._id;
      const toggleData = serviceToggles.get(carrier.carrierAccountId);
      if (toggleData) {
        const isExpanded = expandedRows.has(key);
        expansions[index] = {
          expanded: isExpanded,
          numExpansions: isExpanded ? toggleData.serviceLevels.length : 0,
        };
      }
    }
    return expansions;
  }, [serviceToggles, visibleCarriers, expandedRows]);

  return (
    <>
      {presentation === "cards" ? (
        <Flex dir="column" gap="lg" w="full">
          {cardVariants.length === 0 && (
            <Text fontSize="sm" textColor="secondary">
              This carrier can't be added self-serve for your team. Contact Redo
              support to set it up.
            </Text>
          )}
          {cardVariants.map((carrier) => (
            <CarrierVariantCard
              accounts={(visibleCarriers ?? []).filter(
                (account) => account.redoCarrierName === carrier,
              )}
              addable={addableCardVariants.includes(carrier)}
              addFormValid={addFormValid}
              adding={addingVariant === carrier}
              addSubmitting={addSubmitting}
              carrier={carrier}
              carrierAccountType={carrierAccountType}
              createTestLabelHandler={createTestLabelHandler}
              createTestLabelPending={createTestLabelPending}
              handleAuthenticateCarrier={handleAuthenticateCarrier}
              handleUnverifyContractRates={handleUnverifyContractRates}
              handleVerifyContractRates={handleVerifyContractRates}
              key={carrier}
              loading={savedCarriers === null}
              newCarrier={newCarrier}
              onAddVariant={onAddVariant}
              onCancelAdd={onCancelAdd}
              onRequestDeleteCarrier={setCarrierToDelete}
              onServiceLevelToggle={onServiceLevelToggle}
              onSubmitAdd={onSubmitAdd}
              onUseMfaBackup={onUseMfaBackup}
              serviceToggles={serviceToggles}
              unverifyContractRatesPending={unverifyContractRatesPending}
              verifyContractRatesPending={verifyContractRatesPending}
            />
          ))}
        </Flex>
      ) : (
        <Flex align="flex-start" gap="none" justify="center" pt="3xl" w="full">
          <Flex dir="column" gap="none">
            <Flex
              bgColor="primary"
              borderBottomWidth="none"
              borderColor="primary"
              borderStyle="solid"
              borderWidth="1px"
              bottomLeftRadius="none"
              bottomRightRadius="none"
              gap="none"
              radius="sm"
            >
              <Flex dir="row" justify="space-between" p="xl" w="full">
                <Text fontSize="md" fontWeight="semibold" textColor="primary">
                  {tableTitle}
                </Text>
                {carrierOptions.length > 0 && (
                  <RedoButton
                    hierarchy="secondary"
                    onClick={() => setAddCarrierModalOpen(true)}
                    text="+ Add new carrier account"
                  />
                )}
              </Flex>
            </Flex>
            <RedoTable
              additionalClasses={styles.standaloneTable}
              emptyStateContent={
                <EmptyStateContent
                  carrierAccountType={carrierAccountType}
                  savedCarriers={visibleCarriers}
                />
              }
              layout={carriersTableLayout(
                handleAuthenticateCarrier,
                setCarrierToDelete,
                carrierAccountType,
                createTestLabelPending,
                createTestLabelHandler,
                onUseMfaBackup,
                handleVerifyContractRates,
                handleUnverifyContractRates,
                verifyContractRatesPending,
                unverifyContractRatesPending,
                serviceToggles,
                toggleRowExpansion,
                expandedRows,
                onServiceLevelToggle,
              )}
              rowsExpansions={rowsExpansions}
              rowsOfData={visibleCarriers ?? []}
              size="md"
            />
          </Flex>
        </Flex>
      )}
      {presentation === "table" && (
        <AddCarrierModal
          carrierAccountType={carrierAccountType}
          carrierOptions={carrierOptions}
          isOpen={addCarrierModalOpen}
          newCarrier={newCarrier}
          onAddCarrier={handleAddCarrier}
          onClose={onCloseAddCarrierModal}
          onOpenEasypostFedexMfaForCreate={onOpenEasypostFedexMfaForCreate}
          onOpenFedexMfaModal={onOpenFedexMfaModal}
          selectedCarrier={selectedCarrier}
          sendcloudByoaEnabled={sendcloudByoaEnabled}
          setSelectedCarrier={setSelectedCarrier}
        />
      )}
      {carrierToDelete && (
        <RedoModal
          isOpen
          onModalCloseRequested={() => setCarrierToDelete(undefined)}
          primaryButton={{
            text: "Delete",
            theme: "destructive",
            onClick: () => {
              handleDeleteCarrier(carrierToDelete);
              setCarrierToDelete(undefined);
            },
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => setCarrierToDelete(undefined),
          }}
          subtitle={`This permanently removes "${carrierToDelete.name}". This can't be undone.`}
          theme="error"
          title="Delete carrier account?"
        />
      )}
    </>
  );
});

const EmptyStateContent = memo(function EmptyStateContent({
  savedCarriers,
  carrierAccountType,
}: {
  savedCarriers: ICarrierAccount[] | null;
  carrierAccountType: CarrierAccountType;
}) {
  if (savedCarriers?.length) {
    return undefined;
  }

  return (
    <Flex
      align="center"
      bgColor="primary"
      borderColor="primary"
      borderStyle="solid"
      borderWidth="1px"
      bottomLeftRadius="sm"
      bottomRightRadius="sm"
      dir="column"
      justify="flex-start"
      px="2xl"
      py="2xl"
      style={{ width: "100%", marginTop: "-5px", zIndex: 1 }}
    >
      {!savedCarriers ? (
        <LoadingRedoAnimation className={styles.loadingSpinner} />
      ) : (
        <Text fontSize="md" fontWeight="semibold" textColor="primary">
          {(() => {
            switch (carrierAccountType) {
              case CarrierAccountType.MERCHANT:
                return "No carriers found";
              case CarrierAccountType.REDO_CHILD:
                return "No Redo child carriers found";
              default:
                assertNever(carrierAccountType);
            }
          })()}
        </Text>
      )}
    </Flex>
  );
});

function carriersTableLayout(
  handleAuthenticateCarrier: (carrierAccount: ICarrierAccount) => void,
  onRequestDeleteCarrier: (carrierAccount: ICarrierAccount) => void,
  carrierAccountType: CarrierAccountType,
  createTestLabelPending?: boolean,
  createTestLabelHandler?: (carrierAccount: ICarrierAccount) => void,
  onUseMfaBackup?: (carrierAccount: ICarrierAccount) => void,
  handleVerifyContractRates?: (
    carrierAccount: ICarrierAccount,
  ) => Promise<void>,
  handleUnverifyContractRates?: (
    carrierAccount: ICarrierAccount,
  ) => Promise<void>,
  verifyContractRatesPending?: boolean,
  unverifyContractRatesPending?: boolean,
  serviceToggles?: Map<string, CarrierServiceToggles>,
  toggleRowExpansion?: (key: string) => void,
  expandedRows?: Set<string>,
  onServiceLevelToggle?: (
    key: string,
    carrier: Carrier,
    service: Service,
    enabled: boolean,
  ) => void,
): RedoTableLayout<ICarrierAccount> {
  const hasServiceToggles =
    serviceToggles && toggleRowExpansion && expandedRows;

  const columns: RedoTableLayout<ICarrierAccount>["columns"] = [
    ...(hasServiceToggles
      ? [
          {
            key: "expand",
            widthMode: { type: "fixed" as const, width: "40px" },
            renderHeaderCell: () => (
              <StandardRedoTableHeaderCell headerText="" />
            ),
            renderNormalCell: ({ row }: { row: ICarrierAccount }) => {
              if (!serviceToggles?.get(row.carrierAccountId)) {
                return <RedoTablePaddedCell />;
              }
              const isExpanded = expandedRows.has(row._id);
              return (
                <RedoTablePaddedCell>
                  <Flex
                    align="center"
                    justify="center"
                    onClick={() => toggleRowExpansion(row._id)}
                    style={{ cursor: "pointer" }}
                    w="full"
                  >
                    {isExpanded ? (
                      <ChevronDownIcon height={16} width={16} />
                    ) : (
                      <ChevronRightIcon height={16} width={16} />
                    )}
                  </Flex>
                </RedoTablePaddedCell>
              );
            },
          },
        ]
      : []),

    {
      key: "name",
      widthMode: { type: "fixed" as const, width: "400px" },
      renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="Name" />,
      renderNormalCell: ({ row }: { row: ICarrierAccount }) => {
        const carrier = redoCarrierNameToCarrier[row.redoCarrierName];
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        const carrierTrademarkText = carrierToTrademarkText[carrier];

        const isUspsDirectAccount = row.provider === RateProvider.USPS;
        const contractRatesLinked =
          row.provider === RateProvider.USPS && row.contractRatesLinked;

        return (
          <RedoTablePaddedCell>
            <Flex align="center" dir="row" gap="lg">
              <ShippingLogo
                carrier={carrier ?? row.redoCarrierName}
                size={LogoSize.MEDIUM}
              />
              <Flex dir="column" gap="none" py="sm">
                <Text fontWeight="semibold">{row.name}</Text>
                {carrierTrademarkText && (
                  <Text
                    fontSize="xxs"
                    pr="sm"
                    style={{ textWrap: "wrap" }}
                    textColor="tertiary"
                  >
                    {carrierTrademarkText}
                  </Text>
                )}
                {isUspsDirectAccount && (
                  <Text
                    fontSize="xxs"
                    textColor={contractRatesLinked ? "success" : "error"}
                  >
                    Contract rates verified:{" "}
                    {contractRatesLinked ? "true" : "false"}
                  </Text>
                )}
              </Flex>
            </Flex>
          </RedoTablePaddedCell>
        );
      },
      renderExpandedCell: serviceToggles
        ? ({
            row,
            expansionIndex,
          }: {
            row: ICarrierAccount;
            expansionIndex: number;
          }) => {
            const toggleData = serviceToggles.get(row.carrierAccountId);
            if (!toggleData) {
              return null;
            }
            const serviceLevel = toggleData.serviceLevels[expansionIndex];
            if (!serviceLevel) {
              return null;
            }
            return (
              <RedoTablePaddedCell>
                <Flex align="center" pl="3xl">
                  <Text fontSize="sm">{serviceLevel.serviceDisplayName}</Text>
                </Flex>
              </RedoTablePaddedCell>
            );
          }
        : undefined,
    },
    ...(hasServiceToggles
      ? [
          {
            key: "services",
            widthMode: { type: "fixed" as const, width: "140px" },
            renderHeaderCell: () => (
              <StandardRedoTableHeaderCell headerText="Services" />
            ),
            renderNormalCell: ({ row }: { row: ICarrierAccount }) => {
              const toggleData = serviceToggles.get(row.carrierAccountId);
              if (!toggleData) {
                return null;
              }
              return (
                <RedoTablePaddedCell>
                  <Text
                    fontSize="sm"
                    hoverTextColor="brand-primary"
                    onClick={() => toggleRowExpansion(row._id)}
                    style={{ cursor: "pointer" }}
                    textColor="tertiary"
                    textDecoration="underline"
                  >
                    {toggleData.enabledCount} of {toggleData.totalCount}
                  </Text>
                </RedoTablePaddedCell>
              );
            },
          },
        ]
      : []),

    {
      key: "download-test-label",
      widthMode: { type: "fixed" as const, width: "180px" },
      renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="" />,
      renderNormalCell: ({ row }: { row: ICarrierAccount }) => {
        const isRedoFirstMile =
          carrierAccountType === CarrierAccountType.REDO_CHILD &&
          row.redoCarrierName === RedoCarrierName.FIRST_MILE;

        return (
          <RedoTablePaddedCell>
            {isRedoFirstMile && createTestLabelHandler && (
              <RedoButton
                hierarchy="primary"
                onClick={() => createTestLabelHandler(row)}
                pending={createTestLabelPending}
                size="sm"
                text="Download Test Label"
              />
            )}
          </RedoTablePaddedCell>
        );
      },
    },
    {
      key: "authenticate",
      widthMode: { type: "fixed" as const, width: "210px" },
      renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="" />,
      renderNormalCell: ({ row }: { row: ICarrierAccount }) => {
        return (
          <RedoTablePaddedCell>
            <AuthenticateButton
              carrierAccount={row}
              handleAuthenticateCarrier={handleAuthenticateCarrier}
              handleUnverifyContractRates={handleUnverifyContractRates}
              handleVerifyContractRates={handleVerifyContractRates}
              onUseMfaBackup={onUseMfaBackup}
              unverifyContractRatesPending={unverifyContractRatesPending}
              verifyContractRatesPending={verifyContractRatesPending}
            />
          </RedoTablePaddedCell>
        );
      },
    },
    {
      key: "delete",
      widthMode: { type: "fixed" as const, width: "60px" },
      renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="" />,
      renderNormalCell: ({ row }: { row: ICarrierAccount }) => (
        <RedoTablePaddedCell>
          <TrashIcon
            onClick={() => onRequestDeleteCarrier(row)}
            style={{ cursor: "pointer" }}
          />
        </RedoTablePaddedCell>
      ),
      renderExpandedCell:
        serviceToggles && onServiceLevelToggle
          ? ({
              row,
              expansionIndex,
            }: {
              row: ICarrierAccount;
              expansionIndex: number;
            }) => {
              const toggleData = serviceToggles.get(row.carrierAccountId);
              if (!toggleData) {
                return null;
              }
              const serviceLevel = toggleData.serviceLevels[expansionIndex];
              if (!serviceLevel) {
                return null;
              }
              return (
                <RedoTablePaddedCell>
                  <RedoToggle
                    setValue={(enabled) =>
                      onServiceLevelToggle(
                        row.carrierAccountId,
                        serviceLevel.carrier,
                        serviceLevel.service,
                        enabled,
                      )
                    }
                    value={serviceLevel.enabled}
                  />
                </RedoTablePaddedCell>
              );
            }
          : undefined,
    },
  ];

  return { columns };
}

function AuthenticateButton({
  carrierAccount,
  handleAuthenticateCarrier,
  handleVerifyContractRates,
  handleUnverifyContractRates,
  onUseMfaBackup,
  verifyContractRatesPending,
  unverifyContractRatesPending,
}: {
  carrierAccount: ICarrierAccount;
  handleAuthenticateCarrier: (carrierAccount: ICarrierAccount) => void;
  handleVerifyContractRates?: (
    carrierAccount: ICarrierAccount,
  ) => Promise<void>;
  handleUnverifyContractRates?: (
    carrierAccount: ICarrierAccount,
  ) => Promise<void>;
  onUseMfaBackup?: (carrierAccount: ICarrierAccount) => void;
  verifyContractRatesPending?: boolean;
  unverifyContractRatesPending?: boolean;
}) {
  const [commandMenuOpen, setCommandMenuOpen] = useState(false);
  const [commandMenuAnchor, setCommandMenuAnchor] =
    useState<HTMLElement | null>(null);

  const needsAuthentication =
    (carrierAccount.provider === RateProvider.EASYPOST &&
      carrierAccount.oauthUrl &&
      carrierAccount.oauthStatus !== EasypostOAuthStatus.APPROVED) ||
    (carrierAccount.provider === RateProvider.FEDEX &&
      !carrierAccount.authenticated) ||
    (carrierAccount.provider === RateProvider.USPS &&
      carrierAccount.oauthStatus !== UspsOAuthStatus.APPROVED);

  const needsEasypostFedexMfa =
    carrierAccount.provider === RateProvider.EASYPOST &&
    (carrierAccount.easypostCarrierType === EasyPostCarrierType.FEDEX ||
      carrierAccount.easypostCarrierType ===
        EasyPostCarrierType.FEDEX_SMARTPOST) &&
    !carrierAccount.mfaComplete;

  const canUseMfaBackup =
    carrierAccount.provider === RateProvider.FEDEX &&
    carrierAccount.canUseMfaBackup;

  // Always give an option to re-authenticate USPS carrier accounts. This is because USPS
  // refresh tokens expire after 7 days if we don't auto refresh, so we need to give the user
  // the option to re-authenticate as easily as possible just in case
  if (
    carrierAccount.provider === RateProvider.USPS &&
    carrierAccount.oauthStatus === UspsOAuthStatus.APPROVED
  ) {
    const commandMenuItems: RedoCommandMenuItem[] = [
      {
        text: "Verify Contract Rates",
        onClick: () => handleVerifyContractRates?.(carrierAccount),
      },
      {
        text: "Mark Contract Rates Unverified",
        onClick: () => handleUnverifyContractRates?.(carrierAccount),
        disabled: !carrierAccount.contractRatesLinked,
      },
      {
        text: "Re-Authenticate",
        onClick: () => handleAuthenticateCarrier(carrierAccount),
      },
    ];

    return (
      <div ref={setCommandMenuAnchor}>
        <RedoButtonDropdown
          dropdownOpen={commandMenuOpen}
          hierarchy="secondary"
          onClick={() => handleVerifyContractRates?.(carrierAccount)}
          pending={verifyContractRatesPending || unverifyContractRatesPending}
          setDropdownOpen={setCommandMenuOpen}
          size="sm"
          text="Verify Contract Rates"
        >
          <RedoCommandMenu
            anchor={commandMenuAnchor}
            items={commandMenuItems}
            open={commandMenuOpen}
            setOpen={setCommandMenuOpen}
          />
        </RedoButtonDropdown>
      </div>
    );
  }
  return (
    <>
      {needsEasypostFedexMfa && (
        <RedoButton
          hierarchy="primary"
          onClick={() => handleAuthenticateCarrier(carrierAccount)}
          size="sm"
          text="Enable MFA"
        />
      )}
      {!needsEasypostFedexMfa && needsAuthentication && !canUseMfaBackup && (
        <RedoButton
          hierarchy="primary"
          onClick={() => handleAuthenticateCarrier(carrierAccount)}
          size="sm"
          text="Authenticate"
        />
      )}
      {!needsEasypostFedexMfa && needsAuthentication && canUseMfaBackup && (
        <RedoButton
          hierarchy="primary"
          onClick={() => onUseMfaBackup?.(carrierAccount)}
          size="sm"
          text="Use MFA Backup"
        />
      )}
    </>
  );
}

/** Per-variant card: the add form expands inline; expanding "Manage" shows the same controls as the table. */
const CarrierVariantCard = memo(function CarrierVariantCard({
  carrier,
  accounts,
  loading,
  carrierAccountType,
  addable,
  adding,
  addFormValid,
  addSubmitting,
  newCarrier,
  onAddVariant,
  onCancelAdd,
  onSubmitAdd,
  onRequestDeleteCarrier,
  handleAuthenticateCarrier,
  handleVerifyContractRates,
  handleUnverifyContractRates,
  verifyContractRatesPending,
  unverifyContractRatesPending,
  onUseMfaBackup,
  createTestLabelPending,
  createTestLabelHandler,
  serviceToggles,
  onServiceLevelToggle,
}: {
  carrier: RedoCarrierName;
  accounts: ICarrierAccount[];
  loading: boolean;
  carrierAccountType: CarrierAccountType;
  addable: boolean;
  adding: boolean;
  addFormValid: boolean;
  addSubmitting: boolean;
  newCarrier: CarrierInputForm;
  onAddVariant: (carrier: RedoCarrierName) => void;
  onCancelAdd: () => void;
  onSubmitAdd: () => Promise<void>;
  onRequestDeleteCarrier: (carrierAccount: ICarrierAccount) => void;
  handleAuthenticateCarrier: (carrierAccount: ICarrierAccount) => void;
  handleVerifyContractRates?: (
    carrierAccount: ICarrierAccount,
  ) => Promise<void>;
  handleUnverifyContractRates?: (
    carrierAccount: ICarrierAccount,
  ) => Promise<void>;
  verifyContractRatesPending?: boolean;
  unverifyContractRatesPending?: boolean;
  onUseMfaBackup?: (carrierAccount: ICarrierAccount) => void;
  createTestLabelPending?: boolean;
  createTestLabelHandler?: (carrierAccount: ICarrierAccount) => void;
  serviceToggles?: Map<string, CarrierServiceToggles>;
  onServiceLevelToggle?: (
    key: string,
    carrier: Carrier,
    service: Service,
    enabled: boolean,
  ) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const connected = accounts.length > 0;

  return (
    <Flex
      bgColor="primary"
      borderColor="primary"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="none"
      radius="md"
      w="full"
    >
      <Flex align="center" justify="space-between" p="xl" w="full">
        <Flex align="center" gap="lg">
          <ShippingLogo
            carrier={carrierCardLogoCarrier(carrier)}
            size={LogoSize.MEDIUM}
          />
          <Flex dir="column" gap="none">
            <Text fontSize="sm" fontWeight="semibold">
              {carrierCardLabel(carrier)}
            </Text>
            {!loading && (
              <Text
                fontSize="xs"
                textColor={connected ? "success" : "tertiary"}
              >
                {connected
                  ? `Connected · ${accounts.length} account${accounts.length === 1 ? "" : "s"}`
                  : "Not connected"}
              </Text>
            )}
          </Flex>
        </Flex>
        <Flex align="center" gap="sm">
          {connected && (
            <RedoButton
              hierarchy="tertiary"
              IconTrailing={expanded ? ChevronDownIcon : ChevronRightIcon}
              onClick={() => setExpanded((prev) => !prev)}
              size="sm"
              text="Manage"
            />
          )}
          {addable && (
            <RedoButton
              hierarchy={connected ? "secondary" : "primary"}
              onClick={() => (adding ? onCancelAdd() : onAddVariant(carrier))}
              size="sm"
              text={adding ? "Cancel" : "Add account"}
            />
          )}
        </Flex>
      </Flex>
      {adding && (
        <Flex
          borderColor="primary"
          borderStyle="solid"
          borderTopWidth="1px"
          dir="column"
          gap="xl"
          p="xl"
          w="full"
        >
          <AddCarrierForm newCarrier={newCarrier} />
          <Flex gap="md" justify="flex-end" w="full">
            <RedoButton
              disabled={addSubmitting}
              hierarchy="secondary"
              onClick={onCancelAdd}
              size="sm"
              text="Cancel"
            />
            <RedoButton
              disabled={!addFormValid}
              hierarchy="primary"
              onClick={() => void onSubmitAdd()}
              pending={addSubmitting}
              size="sm"
              text="Add carrier account"
            />
          </Flex>
        </Flex>
      )}
      {expanded && connected && (
        <Flex
          borderColor="primary"
          borderStyle="solid"
          borderTopWidth="1px"
          dir="column"
          gap="xl"
          p="xl"
          w="full"
        >
          {accounts.map((account) => (
            <CardAccountRow
              account={account}
              carrierAccountType={carrierAccountType}
              createTestLabelHandler={createTestLabelHandler}
              createTestLabelPending={createTestLabelPending}
              handleAuthenticateCarrier={handleAuthenticateCarrier}
              handleUnverifyContractRates={handleUnverifyContractRates}
              handleVerifyContractRates={handleVerifyContractRates}
              key={account._id}
              onRequestDeleteCarrier={onRequestDeleteCarrier}
              onServiceLevelToggle={onServiceLevelToggle}
              onUseMfaBackup={onUseMfaBackup}
              serviceToggles={serviceToggles}
              unverifyContractRatesPending={unverifyContractRatesPending}
              verifyContractRatesPending={verifyContractRatesPending}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
});

const CardAccountRow = memo(function CardAccountRow({
  account,
  carrierAccountType,
  onRequestDeleteCarrier,
  handleAuthenticateCarrier,
  handleVerifyContractRates,
  handleUnverifyContractRates,
  verifyContractRatesPending,
  unverifyContractRatesPending,
  onUseMfaBackup,
  createTestLabelPending,
  createTestLabelHandler,
  serviceToggles,
  onServiceLevelToggle,
}: {
  account: ICarrierAccount;
  carrierAccountType: CarrierAccountType;
  onRequestDeleteCarrier: (carrierAccount: ICarrierAccount) => void;
  handleAuthenticateCarrier: (carrierAccount: ICarrierAccount) => void;
  handleVerifyContractRates?: (
    carrierAccount: ICarrierAccount,
  ) => Promise<void>;
  handleUnverifyContractRates?: (
    carrierAccount: ICarrierAccount,
  ) => Promise<void>;
  verifyContractRatesPending?: boolean;
  unverifyContractRatesPending?: boolean;
  onUseMfaBackup?: (carrierAccount: ICarrierAccount) => void;
  createTestLabelPending?: boolean;
  createTestLabelHandler?: (carrierAccount: ICarrierAccount) => void;
  serviceToggles?: Map<string, CarrierServiceToggles>;
  onServiceLevelToggle?: (
    key: string,
    carrier: Carrier,
    service: Service,
    enabled: boolean,
  ) => void;
}) {
  const isUspsDirectAccount = account.provider === RateProvider.USPS;
  const contractRatesLinked =
    isUspsDirectAccount && account.contractRatesLinked;
  const isRedoFirstMile =
    carrierAccountType === CarrierAccountType.REDO_CHILD &&
    account.redoCarrierName === RedoCarrierName.FIRST_MILE;
  const toggleData = serviceToggles?.get(account.carrierAccountId);

  return (
    <Flex dir="column" gap="md" w="full">
      <Flex align="center" justify="space-between" w="full">
        <Flex dir="column" gap="none">
          <Text fontSize="sm" fontWeight="semibold">
            {account.name}
          </Text>
          {isUspsDirectAccount && (
            <Text
              fontSize="xxs"
              textColor={contractRatesLinked ? "success" : "error"}
            >
              Contract rates verified: {contractRatesLinked ? "true" : "false"}
            </Text>
          )}
        </Flex>
        <Flex align="center" gap="sm">
          {isRedoFirstMile && createTestLabelHandler && (
            <RedoButton
              hierarchy="primary"
              onClick={() => createTestLabelHandler(account)}
              pending={createTestLabelPending}
              size="sm"
              text="Download Test Label"
            />
          )}
          <AuthenticateButton
            carrierAccount={account}
            handleAuthenticateCarrier={handleAuthenticateCarrier}
            handleUnverifyContractRates={handleUnverifyContractRates}
            handleVerifyContractRates={handleVerifyContractRates}
            onUseMfaBackup={onUseMfaBackup}
            unverifyContractRatesPending={unverifyContractRatesPending}
            verifyContractRatesPending={verifyContractRatesPending}
          />
          <Flex
            align="center"
            cursor="pointer"
            onClick={() => onRequestDeleteCarrier(account)}
          >
            <TrashIcon />
          </Flex>
        </Flex>
      </Flex>
      {toggleData && onServiceLevelToggle && (
        <Flex dir="column" gap="sm" pl="lg" w="full">
          <Text fontSize="xs" textColor="tertiary">
            Services · {toggleData.enabledCount} of {toggleData.totalCount}{" "}
            enabled
          </Text>
          {toggleData.serviceLevels.map((serviceLevel) => (
            <Flex
              align="center"
              justify="space-between"
              key={`${serviceLevel.carrier}-${serviceLevel.service}`}
              w="full"
            >
              <Text fontSize="sm">{serviceLevel.serviceDisplayName}</Text>
              <RedoToggle
                setValue={(enabled) =>
                  onServiceLevelToggle(
                    account.carrierAccountId,
                    serviceLevel.carrier,
                    serviceLevel.service,
                    enabled,
                  )
                }
                value={serviceLevel.enabled}
              />
            </Flex>
          ))}
        </Flex>
      )}
    </Flex>
  );
});
